// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { createRacePromise, createTimeoutPromise } from "@nevware21/ts-async";
import { asString, dumpObj, isError, isObject, isPrimitive, safe, safeGetLazy } from "@nevware21/ts-utils";
import { _DYN_NAME, _DYN_STRINGIFY, _DYN_TO_STRING } from "../__DynamicConstants";
import { STR_EMPTY } from "../constants/InternalConstants";
import { DBSYSTEMVALUES_DB2, DBSYSTEMVALUES_DERBY, DBSYSTEMVALUES_H2, DBSYSTEMVALUES_HSQLDB, DBSYSTEMVALUES_MARIADB, DBSYSTEMVALUES_MSSQL, DBSYSTEMVALUES_ORACLE, DBSYSTEMVALUES_OTHER_SQL, DBSYSTEMVALUES_SQLITE } from "../otel/attribute/SemanticConventions";
import { getJSON } from "../utils/EnvUtils";
import { handleError } from "./handleErrors";
var _hasJsonStringify = ( /*#__PURE__*/safeGetLazy(function () { return !!getJSON()[_DYN_STRINGIFY /* @min:%2estringify */]; }, null));
var SYNTHETIC_TYPE = "user_agent.synthetic.type";
var CLIENT_DOT = "client.";
var HTTP_DOT = "http.";
var NET_DOT = "net.";
var PEER_DOT = "peer.";
var ATTR_NETWORK_PEER_ADDRESS = "network.peer.address";
var SEMATTRS_NET_PEER_IP = NET_DOT + PEER_DOT + "ip";
var ATTR_CLIENT_ADDRESS = CLIENT_DOT + "address";
var SEMATTRS_HTTP_CLIENT_IP = HTTP_DOT + "client_ip";
var ATTR_USER_AGENT_ORIGINAL = "user_agent.original";
var SEMATTRS_HTTP_USER_AGENT = HTTP_DOT + "user_agent";
var ATTR_URL_FULL = "url.full";
var SEMATTRS_HTTP_URL = HTTP_DOT + "url";
var ATTR_HTTP_REQUEST_METHOD = HTTP_DOT + "request.method";
var SEMATTRS_HTTP_METHOD = HTTP_DOT + "method";
var ATTR_HTTP_RESPONSE_STATUS_CODE = HTTP_DOT + "response.status_code";
var SEMATTRS_HTTP_STATUS_CODE = HTTP_DOT + "status_code";
var ATTR_URL_SCHEME = "url.scheme";
var SEMATTRS_HTTP_SCHEME = HTTP_DOT + "scheme";
var ATTR_URL_PATH = "url.path";
var ATTR_URL_QUERY = "url.query";
var SEMATTRS_HTTP_TARGET = HTTP_DOT + "target";
var ATTR_SERVER_ADDRESS = "server.address";
var SEMATTRS_HTTP_HOST = HTTP_DOT + "host";
var SEMATTRS_NET_PEER_NAME = NET_DOT + PEER_DOT + "name";
var ATTR_CLIENT_PORT = CLIENT_DOT + "port";
var ATTR_SERVER_PORT = "server.port";
var SEMATTRS_NET_PEER_PORT = NET_DOT + PEER_DOT + "port";
var SEMATTRS_PEER_SERVICE = PEER_DOT + "service";
/**
 * Get the URL from the attribute container
 * @param container - The attribute container to extract the URL from
 * @returns The constructed URL string
 */
/* #__NO_SIDE_EFFECTS__ */
export function getUrl(container) {
    var result = "";
    if (container) {
        var httpMethod = getHttpMethod(container);
        if (httpMethod) {
            var httpUrl = getHttpUrl(container);
            if (httpUrl) {
                result = asString(httpUrl);
            }
            else {
                var httpScheme = getHttpScheme(container);
                var httpTarget = getHttpTarget(container);
                if (httpScheme && httpTarget) {
                    var httpHost = getHttpHost(container);
                    if (httpHost) {
                        result = httpScheme + "://" + httpHost + httpTarget;
                    }
                    else {
                        var netPeerPort = getNetPeerPort(container);
                        if (netPeerPort) {
                            var netPeerName = getNetPeerName(container);
                            if (netPeerName) {
                                result = httpScheme + "://" + netPeerName + ":" + netPeerPort + httpTarget;
                            }
                            else {
                                var netPeerIp = getPeerIp(container);
                                if (netPeerIp) {
                                    result = httpScheme + "://" + netPeerIp + ":" + netPeerPort + httpTarget;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return result;
}
/**
 * Gets the synthetic type from the attribute container
 * @param container - The attribute container to extract the synthetic type from
 * @returns The synthetic type value
 */
/* #__NO_SIDE_EFFECTS__ */
export function getSyntheticType(container) {
    return container.get(SYNTHETIC_TYPE);
}
/**
 * Determine if the attribute container represents a synthetic source
 * @param container - The attribute container to check
 * @returns True if the attribute container is from a synthetic source
 */
/* #__NO_SIDE_EFFECTS__ */
export function isSyntheticSource(container) {
    return !!getSyntheticType(container);
}
/**
 * Serialize an attribute value to a string value
 * @param value - The attribute value to serialize
 * @returns The serialized string value
 */
/* #__NO_SIDE_EFFECTS__ */
export function serializeAttribute(value) {
    var result;
    if (isError(value)) {
        result = dumpObj(value);
    }
    else if ((!value[_DYN_TO_STRING /* @min:%2etoString */] || isObject(value)) && _hasJsonStringify.v) {
        result = safe(getJSON()[_DYN_STRINGIFY /* @min:%2estringify */], [value]).v;
    }
    else if (isPrimitive(value)) {
        // We keep primitives as-is, so that the standard attribute types are preserved
        // These are converted as required in the sending channel(s)
        result = value;
    }
    else {
        result = asString(value);
    }
    // Return scalar and undefined values
    return result;
}
/**
 * Peer address of the network connection - IP address or Unix domain socket name.
 *
 * @example 10.1.2.80
 * @example /tmp/my.sock
 * @since 3.4.0
 */
/* #__NO_SIDE_EFFECTS__ */
export function getPeerIp(container) {
    if (container) {
        return container.get(ATTR_NETWORK_PEER_ADDRESS) || container.get(SEMATTRS_NET_PEER_IP);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getLocationIp(container) {
    var result;
    if (container) {
        var httpClientIp = getHttpClientIp(container);
        if (httpClientIp) {
            result = asString(httpClientIp);
        }
        if (!result) {
            var netPeerIp = getPeerIp(container);
            if (netPeerIp) {
                result = asString(netPeerIp);
            }
        }
    }
    return result;
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpClientIp(container) {
    if (container) {
        return container.get(ATTR_CLIENT_ADDRESS) || container.get(SEMATTRS_HTTP_CLIENT_IP);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getUserAgent(container) {
    if (container) {
        return container.get(ATTR_USER_AGENT_ORIGINAL) || container.get(SEMATTRS_HTTP_USER_AGENT);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpUrl(container) {
    // Stable sem conv only supports populating url from `url.full`
    if (container) {
        return container.get(ATTR_URL_FULL) || container.get(SEMATTRS_HTTP_URL);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpMethod(container) {
    if (container) {
        return container.get(ATTR_HTTP_REQUEST_METHOD) || container.get(SEMATTRS_HTTP_METHOD);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpStatusCode(container) {
    if (container) {
        return container.get(ATTR_HTTP_RESPONSE_STATUS_CODE) || container.get(SEMATTRS_HTTP_STATUS_CODE);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpScheme(container) {
    if (container) {
        return container.get(ATTR_URL_SCHEME) || container.get(SEMATTRS_HTTP_SCHEME);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpTarget(container) {
    if (container) {
        return container.get(ATTR_URL_PATH) || container.get(ATTR_URL_QUERY) || container.get(SEMATTRS_HTTP_TARGET);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getHttpHost(container) {
    if (container) {
        return container.get(ATTR_SERVER_ADDRESS) || container.get(SEMATTRS_HTTP_HOST);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getNetPeerName(container) {
    if (container) {
        return container.get(ATTR_CLIENT_ADDRESS) || container.get(SEMATTRS_NET_PEER_NAME);
    }
}
/* #__NO_SIDE_EFFECTS__ */
export function getNetPeerPort(container) {
    if (container) {
        return (container.get(ATTR_CLIENT_PORT) ||
            container.get(ATTR_SERVER_PORT) ||
            container.get(SEMATTRS_NET_PEER_PORT));
    }
}
export function getDependencyTarget(container) {
    var result;
    if (container) {
        var peerService = container.get(SEMATTRS_PEER_SERVICE);
        if (peerService) {
            result = asString(peerService);
        }
        if (!result) {
            var httpHost = getHttpHost(container);
            if (httpHost) {
                result = asString(httpHost);
            }
        }
        if (!result) {
            var httpUrl = getHttpUrl(container);
            if (httpUrl) {
                result = asString(httpUrl);
            }
        }
        if (!result) {
            var netPeerName = getNetPeerName(container);
            if (netPeerName) {
                result = asString(netPeerName);
            }
        }
        if (!result) {
            var netPeerIp = getPeerIp(container);
            if (netPeerIp) {
                result = asString(netPeerIp);
            }
        }
    }
    return result || STR_EMPTY;
}
/** #__NO_SIDE_EFFECTS__ */
export function isSqlDB(dbSystem) {
    return (dbSystem === DBSYSTEMVALUES_DB2 ||
        dbSystem === DBSYSTEMVALUES_DERBY ||
        dbSystem === DBSYSTEMVALUES_MARIADB ||
        dbSystem === DBSYSTEMVALUES_MSSQL ||
        dbSystem === DBSYSTEMVALUES_ORACLE ||
        dbSystem === DBSYSTEMVALUES_SQLITE ||
        dbSystem === DBSYSTEMVALUES_OTHER_SQL ||
        dbSystem === DBSYSTEMVALUES_HSQLDB ||
        dbSystem === DBSYSTEMVALUES_H2);
}
/**
 * Adds a timeout to a promise and rejects if the specified timeout has elapsed.
 * Reports the timeout through the configured error handlers before rejecting.
 *
 * @param handlers - The configured error handlers to notify.
 * @param promise - The promise to guard with the timeout.
 * @param timeout - Timeout in milliseconds before the promise is rejected.
 */
export function callWithTimeout(handlers, promise, timeout) {
    var timeoutMessage = "Operation timed out.";
    var timeoutError = new Error(timeoutMessage);
    timeoutError[_DYN_NAME /* @min:%2ename */] = "TimeoutError";
    timeoutError.__otelTimeout = true;
    var racedPromise = createRacePromise([
        promise,
        createTimeoutPromise(timeout, false, timeoutError)
    ]);
    return racedPromise.catch(function (error) {
        if (error && (error === timeoutError || error.__otelTimeout)) {
            handleError(handlers, timeoutMessage);
        }
        throw error;
    });
}
//# sourceMappingURL=commonUtils.js.map